import cx from 'classnames';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { configureAnchors } from 'react-scrollable-anchor';
import TabView from '../../../components/TabView';
import {
  getCRSClusterViewDescriptionCodesData,
  getCRSFeatureCategoryCodesData,
  getCRSFeatureCodesData,
  getCRSTransactionProductMappingData,
  getCRSTransactionTypesData,
  getCustomerProfileData
} from '../../../redux/actions/crs/crsRiskDetail';
import { getGlobalSettingsPageInfo } from '../../../redux/actions/settings';
import { getCustomerKeyFromURL } from '../../../services/util';
import {
  CRS_CONSTANTS,
  CRS_TAB_PAGEID_LIST
} from '../../constants/crsContants';
import AssocCustomers from './AssocCustomers';
import AssocTransaction from './AssocTransaction';
import CRSDetailHeader from './crsDetailHeader';
import TrendAnalysisComponent from './CRSTrendAnalysis';
import CustomerProfileView from './CustomerProfileView';
import HistAlert from './HistAlert';
import style from './index.scss';
import { processDataFromProps } from './riskDetailUtil';
import ScoreExplainability from './ScoreExplainability';
import { getAMLSCommonAlertDetails } from '../../../redux/actions/common';

const { GET_DATA } = CRS_CONSTANTS;
const GLOBAL_SETTING_PAGE_ID = CRS_TAB_PAGEID_LIST[1];

// const isPrioritizedAlert = true;

const navigationTabList = [
  'Profile',
  'Trend Analysis and Risk Lineage',
  'Score Explainability',
  'Transaction Details',
  'Alert History'
];

const prioritizedNavigationList = [
  'Profile',
  'Trend Analysis and Risk Lineage',
  'Score Explainability',
  'Transaction Details',
  'Alert History'
];

const navigationGSConstants = [
  true, // -> 'Profile',
  true, // -> 'Trend Analysis and Risk Lineage',
  true, // -> 'Score Explainability',
  {
    widgetGroupId: 'CRS_TRANSACTION_DETAILS_GROUP',
    widgetId: 'CRS_TRANSACTION_DETAILS_WIDGET'
  }, // -> 'Transaction Details',
  {
    widgetGroupId: 'CRS_ALERT_HISTORY_GROUP',
    widgetId: 'CRS_ALERT_HISTORY_WIDGET'
  } // -> 'Alert History'
];

const prioritizedNavigationGSConstants = [
  true, // -> 'Profile',
  false, // -> 'Trend Analysis and Risk Lineage',
  true, // -> 'Score Explainability',
  {
    widgetGroupId: 'CRS_TRANSACTION_DETAILS_GROUP',
    widgetId: 'CRS_TRANSACTION_DETAILS_WIDGET'
  }, // -> 'Transaction Details',
  {
    widgetGroupId: 'CRS_ALERT_HISTORY_GROUP',
    widgetId: 'CRS_ALERT_HISTORY_WIDGET'
  } // -> 'Alert History'
];

/**
 * CRSRiskDetail Page contains all the Widgets required for CRS
 */
class CRSRiskDetail extends Component {
  /**
   * constructor
   * @param props
   */
  constructor(props) {
    super(props);
    this.customerKey = props.isEmbedded
      ? props.customerId
      : getCustomerKeyFromURL();
    this.state = {
      selectedTab: 0,
      isPrioritizedAlert: false
    };
  }

  /**
   * componentDidMount
   * Added all the necessary Dispatch Items items
   * Here like CODES API and Mappings
   */
  componentDidMount() {
    window.scrollTo(0, 0);
    configureAnchors({ offset: -60, scrollDuration: 300 });
    this.props.dispatch(
      getCustomerProfileData({ customerKey: this.customerKey }, GET_DATA)
    );
    this.props.dispatch(getCRSFeatureCodesData({}, GET_DATA));
    this.props.dispatch(getCRSTransactionTypesData({}, GET_DATA));
    this.props.dispatch(getCRSFeatureCategoryCodesData({}, GET_DATA));
    this.props.dispatch(getCRSClusterViewDescriptionCodesData({}, GET_DATA));
    this.props.dispatch(getCRSTransactionProductMappingData({}, GET_DATA));

    this.props.dispatch(
      getGlobalSettingsPageInfo({
        pageId: GLOBAL_SETTING_PAGE_ID
      })
    );
    if (this.props.isEmbedded) {
      this.props.dispatch(
        getAMLSCommonAlertDetails({ alertId: this.props.alertId })
      );
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.isEmbedded) {
      this.setState({
        isPrioritizedAlert:
          nextProps?.alertDetails?.data?.alertType === 'CRS_PRIORITISE_ALERT'
            ? true
            : false
      });
    }
    if (this.customerKey !== nextProps?.customerId && nextProps.isEmbedded) {
      const customerKey = nextProps?.customerId;
      window.scrollTo(0, 0);
      configureAnchors({ offset: -60, scrollDuration: 300 });
      this.props.dispatch(getCustomerProfileData({ customerKey }, GET_DATA));
      this.props.dispatch(
        getGlobalSettingsPageInfo({
          pageId: GLOBAL_SETTING_PAGE_ID
        })
      );

      this.customerKey = customerKey;
      this.setState({
        selectedTab: 0
      });

      this.props.dispatch(
        getAMLSCommonAlertDetails({ alertId: nextProps?.alertId })
      );
    }
  }

  onNavigationTabSelect = (index) => {
    this.setState({
      selectedTab: index
    });
  };

  getGSConfig = (q) => {
    if (this.props.settings?.configuration?.data?.uiWidgetGroups) {
      const groups = this.props.settings.configuration.data.uiWidgetGroups;
      return groups
        .find((g) => g.widgetGroupId === q.widgetGroupId)
        ?.widgets.find((w) => w.widgetId === q.widgetId);
    }
    return {};
  };

  isGSVisible = (q) => {
    if (this.props.settings?.configuration?.data?.uiWidgetGroups) {
      const groups = this.props.settings.configuration.data.uiWidgetGroups;
      return groups
        .find((g) => g.widgetGroupId === q.widgetGroupId)
        ?.widgets.find((w) => w.widgetId === q.widgetId)?.isVisible;
    }
    return false;
  };

  /**
   * crsRiskDetailData
   * @returns {div}
   */
  render() {
    const { selectedTab, isPrioritizedAlert } = this.state;
    const { crsRiskDetailData } = this.props;
    const {
      featureCodesResponse,
      crsTransactionTypesResponse,
      crsFeatureCategoryCodesData,
      customerProfileData
    } = processDataFromProps(crsRiskDetailData);

    // select which navigation tab and GS constants to select when prioritized alert is there
    const updatedNavigationTabList = isPrioritizedAlert
      ? prioritizedNavigationList
      : navigationTabList;
    const updatedNavigationGSContants = isPrioritizedAlert
      ? prioritizedNavigationGSConstants
      : navigationGSConstants;

    // add new constants for tabIndexes for diff tabs
    const customerTab = 0;
    const trendAnalysisTab = 1;
    const explainabilityTab = isPrioritizedAlert ? 1 : 2;
    const transactionDetailsTab = isPrioritizedAlert ? 2 : 3;
    const alertHistoryTab = isPrioritizedAlert ? 3 : 4;

    const { data } = this.props.alertDetails;
    const alertDate = data?.alertDate;

    return (
      <div
        className={cx({
          [style.embedContainer]: this.props.isEmbedded
        })}>
        <CRSDetailHeader
          customerProfileData={customerProfileData}
          isEmbedded={this.props.isEmbedded}
          alertId={this.props.alertId}
          alertMetaSuffix={this.props.alertMetaSuffix}
          alertMetaPrefix={this.props.alertMetaPrefix}
          alertDetails={this.props.alertDetails}
        />
        <div className={style.tabViewContainer}>
          <TabView
            defaultIndex={selectedTab}
            tabList={updatedNavigationTabList.filter((tab, index) => {
              if (updatedNavigationGSContants[index] !== true) {
                return this.isGSVisible(updatedNavigationGSContants[index]);
              }
              return updatedNavigationGSContants[index];
            })}
            onTabSelect={this.onNavigationTabSelect}
          />
        </div>
        <div className={style.riskDetailInnerContainer}>
          {selectedTab === 0 && (
            <div id={'section0'}>
              <CustomerProfileView
                customerProfileData={customerProfileData}
                GSConfigIndividual={this.getGSConfig({
                  widgetGroupId: 'CRS_CUSTOMER_PROFILE_GROUP',
                  widgetId: 'CRS_CUSTOMER_DETAILS_WIDGET_I'
                })}
                GSConfigCorporate={this.getGSConfig({
                  widgetGroupId: 'CRS_CUSTOMER_PROFILE_GROUP',
                  widgetId: 'CRS_CUSTOMER_DETAILS_WIDGET_C'
                })}
              />
              <AssocCustomers
                customerKey={this.customerKey}
                GSConfig={this.getGSConfig({
                  widgetGroupId: 'CRS_CUSTOMER_PROFILE_GROUP',
                  widgetId: 'CRS_ASSOCIATED_CUSTOMER_WIDGET'
                })}
              />
            </div>
          )}

          {selectedTab === trendAnalysisTab && !isPrioritizedAlert && (
            // this.isGSVisible(updatedNavigationGSContants[1]) && (
            <div id={'section1'}>
              <TrendAnalysisComponent
                customerKey={this.customerKey}
                crsFeatureCategoryCodesData={crsFeatureCategoryCodesData}
              />
            </div>
          )}

          {selectedTab === explainabilityTab && (
            <div id={isPrioritizedAlert ? 'section1' : 'section2'}>
              <ScoreExplainability
                isPrioritizedAlert={isPrioritizedAlert}
                alertDetails={this.props.alertDetails}
                customerKey={this.customerKey}
                GSConfig={this.getGSConfig({
                  widgetGroupId: 'CRS_SCORE_EXPLANABILITY_GROUP',
                  widgetId: 'CRS_SCORE_EXPLANABILITY_WIDGET'
                })}
              />
            </div>
          )}

          {selectedTab === transactionDetailsTab &&
            this.isGSVisible(updatedNavigationGSContants[3]) && (
              <div id={isPrioritizedAlert ? 'section2' : 'section3'}>
                <AssocTransaction
                  customerKey={this.customerKey}
                  GSConfig={this.getGSConfig({
                    widgetGroupId: 'CRS_TRANSACTION_DETAILS_GROUP',
                    widgetId: 'CRS_TRANSACTION_DETAILS_WIDGET'
                  })}
                  alertDate={alertDate}
                />
              </div>
            )}

          {selectedTab === alertHistoryTab &&
            this.isGSVisible(updatedNavigationGSContants[4]) && (
              <div id={isPrioritizedAlert ? 'section3' : 'section4'}>
                <HistAlert
                  customerKey={this.customerKey}
                  GSConfig={this.getGSConfig({
                    widgetGroupId: 'CRS_ALERT_HISTORY_GROUP',
                    widgetId: 'CRS_ALERT_HISTORY_WIDGET'
                  })}
                />
              </div>
            )}
        </div>
      </div>
    );
  }
}

CRSRiskDetail.propTypes = {
  dispatch: PropTypes.func,
  settings: PropTypes.object,
  crsRiskDetailData: PropTypes.object,
  isEmbedded: PropTypes.bool,
  alertId: PropTypes.string,
  customerId: PropTypes.string,
  alertMetaSuffix: PropTypes.element,
  alertMetaPrefix: PropTypes.element,
  alertDetails: PropTypes.object
};

/**
 * mapStateToProps
 * @param state
 * @param ownProps
 * @returns {{crsRiskDetailData: crsRiskDetailReducer}}
 */
function mapStateToProps(state, ownProps = {}) {
  return {
    crsRiskDetailData: state.crsRiskDetailReducer,
    settings: state.settings,
    alertDetails: state.amlsCommon.alertDetails
  };
}

/**
 * mapDispatchToProps
 * @param dispatch
 * @param ownProps
 * @returns {{dispatch: *, navigateTo: navigateTo}}
 */
function mapDispatchToProps(dispatch, ownProps = {}) {
  return {
    dispatch
  };
}

/**
 * connect function for CRSRiskDetail page
 */
export default connect(mapStateToProps, mapDispatchToProps)(CRSRiskDetail);
